import { useState } from "react";
import { Search, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { MotionButton } from "../common";

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-xs font-semibold text-slate-500">{label}</span>
    {children}
  </label>
);

export const Input = (p) => <input className="field" {...p} />;

export const Select = ({ options, ...p }) => (
  <select className="field appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%2364748b%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[right_0.8rem_center] bg-no-repeat pr-9" {...p}>
    {options.map(([v, l]) => (
      <option key={v} value={v}>{l}</option>
    ))}
  </select>
);

export const STATUS_OPTS = [["active", "Aktif"], ["passive", "Pasif"]];

export function Toolbar({ q, setQ, placeholder, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative sm:w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="field pl-9" data-testid="admin-search-input" />
      </div>
      {action}
    </div>
  );
}

export const Table = ({ head, children, testid }) => (
  <div className="surface overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {head.map((h) => <th key={h} className="px-5 py-3.5">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  </div>
);

export const Row = ({ children, ...p }) => <tr className="transition-colors hover:bg-slate-50/70" {...p}>{children}</tr>;
export const Td = ({ children, className = "" }) => <td className={`px-5 py-3.5 align-middle ${className}`}>{children}</td>;

export const IconBtn = ({ icon: Icon, label, danger, ...p }) => (
  <button title={label} aria-label={label} className={`grid h-9 w-9 place-items-center rounded-lg text-slate-500 transition-[background-color,color,transform] hover:scale-105 active:scale-95 ${danger ? "hover:bg-rose-50 hover:text-rose-600" : "hover:bg-slate-100 hover:text-[var(--ink)]"}`} {...p}>
    <Icon className="h-4 w-4" />
  </button>
);

export function FormDialog({ open, onClose, title, desc, onSubmit, saving, children, wide, testid = "form-dialog" }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto rounded-2xl`} data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold tracking-tight">{title}</DialogTitle>
          {desc && <DialogDescription>{desc}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          {children}
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="btn-ghost h-11 rounded-xl px-5 text-sm font-semibold" data-testid="form-cancel-btn">Vazgeç</button>
            <MotionButton type="submit" loading={saving} data-testid="form-save-btn">Kaydet</MotionButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDelete({ onConfirm, label = "Sil", testid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <IconBtn icon={Trash2} label={label} danger onClick={() => setOpen(true)} data-testid={testid} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Emin misiniz?</DialogTitle>
            <DialogDescription>Bu işlem geri alınamaz.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="btn-ghost h-10 rounded-xl px-4 text-sm font-semibold">Vazgeç</button>
            <MotionButton loading={busy} loadingText="Siliniyor..." className="h-10" data-testid="confirm-delete-btn" onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); setOpen(false); }}>
              {label}
            </MotionButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
